import { create } from 'zustand';

/**
 * Store to manage module preview state
 * This centralizes state for the preview interface including
 * which view is active and selected task information
 */
export const useModulePreviewStore = create((set) => ({
  // Base state
  isOpen: false,
  activeView: 'course', // 'course', 'module', 'task', or 'diff'
  selectedTaskId: null,
  canViewDiff: false, // Whether diff view is available

  // Module data (set via setPreviewData)
  module: null,
  tasks: [],
  sourceModule: null, // Source module for comparison
  sourceTasks: [], // Source tasks for comparison
  allModules: [], // Optional: for context in course view

  // Setters
  setIsOpen: (isOpen) => set({ isOpen }),
  setActiveView: (activeView) => set({ activeView }),
  setSelectedTaskId: (selectedTaskId) => set({ selectedTaskId }),
  setCanViewDiff: (canViewDiff) => set({ canViewDiff }),

  // Data setters
  setPreviewData: (moduleData) => set({
    module: moduleData.module,
    tasks: moduleData.tasks || [],
    sourceModule: moduleData.sourceModule || null,
    sourceTasks: moduleData.sourceTasks || [],
    allModules: moduleData.allModules || [moduleData.module],
    canViewDiff: !!moduleData.sourceModule,
  }),

  // Combined operations

  /**
   * Open preview fresh (e.g. from the task editor Preview button).
   * Always resets nav state so preview starts at the module task-list
   * with no task pre-selected, regardless of what was open previously.
   */
  openPreview: (moduleData) => set({
    isOpen: true,
    module: moduleData.module,
    tasks: moduleData.tasks || [],
    sourceModule: moduleData.sourceModule || null,
    sourceTasks: moduleData.sourceTasks || [],
    allModules: moduleData.allModules || [moduleData.module],
    activeView: 'module',
    selectedTaskId: null,
    canViewDiff: !!moduleData.sourceModule,
  }),

  /**
   * Initialize/update preview data without resetting navigation state.
   * Used internally by ModulePreviewContainer on mount/prop change so
   * that activeView and selectedTaskId survive re-renders when props update.
   */
  initPreview: (moduleData) => set({
    isOpen: true,
    module: moduleData.module,
    tasks: moduleData.tasks || [],
    sourceModule: moduleData.sourceModule || null,
    sourceTasks: moduleData.sourceTasks || [],
    allModules: moduleData.allModules || [moduleData.module],
    canViewDiff: !!moduleData.sourceModule,
  }),

  closePreview: () => set({ isOpen: false }),

  selectTask: (taskId) => set({
    selectedTaskId: taskId,
    // activeView: taskId ? 'task' : get().activeView,
  }),

  // Reset preview state
  resetPreview: () => set({
    activeView: 'course',
    selectedTaskId: null,
  }),
}));

/**
 * Hook for accessing preview navigation state
 * @returns Navigation state and actions for the preview
 */
export const usePreviewNavigation = () => {
  const activeView = useModulePreviewStore((state) => state.activeView);
  const setActiveView = useModulePreviewStore((state) => state.setActiveView);
  const selectedTaskId = useModulePreviewStore((state) => state.selectedTaskId);
  const setSelectedTaskId = useModulePreviewStore((state) => state.setSelectedTaskId);
  const selectTask = useModulePreviewStore((state) => state.selectTask);
  const canViewDiff = useModulePreviewStore((state) => state.canViewDiff);
  const setCanViewDiff = useModulePreviewStore((state) => state.setCanViewDiff);

  return {
    activeView,
    setActiveView,
    selectedTaskId,
    setSelectedTaskId,
    selectTask,
    canViewTask: !!selectedTaskId,
    canViewDiff,
    setCanViewDiff,
  };
};

/**
 * Hook for controlling the preview modal
 * @returns Modal control state and actions
 */
export const usePreviewModal = () => {
  const isOpen = useModulePreviewStore((state) => state.isOpen);
  const openPreview = useModulePreviewStore((state) => state.openPreview);
  const initPreview = useModulePreviewStore((state) => state.initPreview);
  const closePreview = useModulePreviewStore((state) => state.closePreview);
  const resetPreview = useModulePreviewStore((state) => state.resetPreview);

  return {
    isOpen,
    openPreview,
    initPreview,
    closePreview,
    resetPreview,
  };
};

/**
 * Hook for accessing preview content data
 * @returns Module data for the preview
 */
export const usePreviewContent = () => {
  const module = useModulePreviewStore((state) => state.module);
  const tasks = useModulePreviewStore((state) => state.tasks);
  const sourceModule = useModulePreviewStore((state) => state.sourceModule);
  const sourceTasks = useModulePreviewStore((state) => state.sourceTasks);
  const allModules = useModulePreviewStore((state) => state.allModules);
  const selectedTaskId = useModulePreviewStore((state) => state.selectedTaskId);

  // Compute selected task based on ID
  const selectedTask = selectedTaskId
    ? tasks.find((task) => task.id === selectedTaskId)
    : null;

  return {
    module,
    tasks,
    sourceModule,
    sourceTasks,
    allModules,
    selectedTask,
    hasModule: !!module,
    hasDiffData: !!sourceModule,
  };
};

export default useModulePreviewStore;
